/**
 * Status and tier chips for the Wedding Books section.
 *
 * A wedding book's eight statuses do not map onto the shared StatusBadge
 * vocabulary (active/inactive/pending/…), so they get their own chip that reads
 * STATUS_META. Colours are MUI palette KEYS, which means dark mode is automatic
 * and there is not a single hex literal in this file — the two tier accents
 * come from brandColors.
 */

import React from 'react';
import { Chip, Tooltip } from '@mui/material';
import type { ChipProps } from '@mui/material';
import {
  STATUS_META,
  TIER_META,
  TIER_ACCENT_COLOR,
  GUEST_STATUS_META,
} from './weddingFormat';
import type { BookStatus, GuestStatus, Tier } from './weddingApi';

export const WeddingStatusChip: React.FC<{
  status: BookStatus;
  size?: ChipProps['size'];
}> = ({ status, size = 'small' }) => {
  const meta = STATUS_META[status];
  if (!meta) return <Chip label={status} size={size} />;
  return (
    <Tooltip title={meta.description}>
      <Chip label={meta.label} color={meta.color} size={size} variant="filled" />
    </Tooltip>
  );
};

export const WeddingTierChip: React.FC<{
  tier: Tier;
  size?: ChipProps['size'];
}> = ({ tier, size = 'small' }) => {
  const meta = TIER_META[tier];
  if (!meta) return <Chip label={tier} size={size} />;
  const accent = TIER_ACCENT_COLOR[meta.accent];
  return (
    <Chip
      label={meta.label}
      size={size}
      variant={meta.variant}
      sx={
        accent
          ? {
              borderColor: accent,
              // A filled premium chip needs a readable foreground; an outlined
              // one only tints its border and label.
              ...(meta.variant === 'filled'
                ? { backgroundColor: accent, color: 'common.white' }
                : { color: accent }),
            }
          : undefined
      }
    />
  );
};

export const GuestStatusChip: React.FC<{
  status: GuestStatus;
  size?: ChipProps['size'];
}> = ({ status, size = 'small' }) => {
  const meta = GUEST_STATUS_META[status];
  if (!meta) return <Chip label={status} size={size} />;
  return <Chip label={meta.label} color={meta.color} size={size} variant="outlined" />;
};

export default WeddingStatusChip;
